import { FunctionComponent, useRef, useEffect, useCallback } from 'react';
import maplibregl, { LngLatLike, MapMouseEvent } from 'maplibre-gl';

import {
  ARTIFACTS_ICON_DICTIONARY,
  LAYER_VISIBILITY_ZOOM,
  LAYERS,
  SOURCE_IDS
} from './mapConstants';

import {
  addMoveMouseEvent,
  selectArtifact,
  selectFullElectricFeederLines,
  selectFullElectricFeederPolygon,
  deselectAllFeatures
} from './mapEventUtils';
import { MapProps } from './mapTypes';
import { removeLines, removePoints, removePolygons } from './mapLayerUtils';

import 'maplibre-gl/dist/maplibre-gl.css';
import './map.css';
import { drawLines, drawPoints, drawPolygon } from './mapGeoJSONUtils';

// MAP CREATION ==============
const newMap = (mapContainer: HTMLDivElement, props: { center: LngLatLike; zoom: number }) =>
  new maplibregl.Map({
    container: mapContainer,
    style: {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '&copy; OpenStreetMap Contributors',
          maxzoom: 19
        }
      },
      layers: [
        {
          id: 'osm',
          type: 'raster',
          source: 'osm',
          // Mapa base gris claro y de bajo contraste para que la red (de colores) destaque.
          paint: {
            'raster-saturation': -0.95,
            'raster-contrast': -0.2,
            'raster-brightness-min': 0.15
          }
        }
      ]
    },
    center: props.center,
    zoom: props.zoom
  });

const getLatLngCoordenates = (coordenates?: { lat: number; lng: number }): LngLatLike => {
  const lat = coordenates?.lat || -33.4459;
  const lng = coordenates?.lng || -70.6605;
  return [lng, lat];
};

const Map: FunctionComponent<MapProps> = ({
  mapPointFeatures,
  mapLineFeatures,
  mapPolygonFeatures,
  electricFeeders,
  mapCenter,
  mapZoom,
  onArtifactClick,
  exportImage
}) => {
  const initialZoomLevel = 11.09;

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);

  // Refs para props de callbacks — evita incluirlos como deps de efectos,
  // lo que causaba re-ejecuciones innecesarias (flickering, pérdida de highlights).
  const electricFeedersRef = useRef(electricFeeders);
  const onArtifactClickRef = useRef(onArtifactClick);
  electricFeedersRef.current = electricFeeders;
  onArtifactClickRef.current = onArtifactClick;

  const exportImgCallback = useCallback(() => {
    map.current?.once('idle', () => {
      const canvas = map.current?.getCanvas();
      if (canvas && exportImage) {
        const image = canvas.toDataURL();
        exportImage(image);
      }
    });
  }, [exportImage]);

  // Efecto 1: Crear el mapa UNA sola vez al montar el componente.
  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = newMap(mapContainer.current, {
      center: getLatLngCoordenates(mapCenter),
      zoom: mapZoom ?? initialZoomLevel
    });

    addMoveMouseEvent(map.current, () => undefined);
    map.current.addControl(new maplibregl.NavigationControl());

    exportImgCallback();

    return () => {
      map.current?.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Efecto 2: Navegacion — mueve centro/zoom sin recrear el mapa.
  useEffect(() => {
    if (!map.current) return;
    map.current.setCenter(getLatLngCoordenates(mapCenter) as LngLatLike);
    map.current.setZoom(mapZoom ?? initialZoomLevel);
    exportImgCallback();
  }, [mapCenter, mapZoom, exportImgCallback]);

  // Efecto 3: Sincronizar capas — SOLO cuando los datos de features cambian.
  // Usa drawLines/drawPolygon/drawPoints directamente (que hacen setData en sources
  // existentes) en vez de drawMapLayers (que destruye y recrea todo, perdiendo
  // feature states/highlights y causando flickering por la recarga de imágenes).
  useEffect(() => {
    if (!map.current) return;
    const m = map.current;
    let cancelled = false;

    const syncLayers = async () => {
      if (cancelled || !map.current) return;

      if (mapLineFeatures.length > 0) {
        drawLines(map, mapLineFeatures, SOURCE_IDS.lines);
      } else {
        removeLines(map);
      }

      if (mapPolygonFeatures.length > 0) {
        drawPolygon(map, mapPolygonFeatures, SOURCE_IDS.polygon);
      } else {
        removePolygons(map);
      }

      if (mapPointFeatures.length > 0) {
        await drawPoints(map, mapPointFeatures, ARTIFACTS_ICON_DICTIONARY, SOURCE_IDS.points);
      } else {
        removePoints(map);
      }

      exportImgCallback();
    };

    if (m.isStyleLoaded()) {
      syncLayers();
    } else {
      m.once('style.load', syncLayers);
    }

    return () => {
      cancelled = true;
    };
  }, [mapPointFeatures, mapLineFeatures, mapPolygonFeatures, exportImgCallback]);

  // Efecto 4: Click handler — se registra UNA vez al montar.
  // Usa refs para electricFeeders y onArtifactClick para siempre tener los
  // valores más recientes sin necesidad de re-registrar el handler (lo que
  // antes causaba que drawMapLayers destruyera capas en cada render).
  useEffect(() => {
    if (!map.current) return;
    const m = map.current;

    const layerNames = [LAYERS.polygonFill, LAYERS.lines, LAYERS.points];

    const handleClick = (e: MapMouseEvent) => {
      if (!map.current) return;

      const existingLayers = layerNames.filter((l) => map.current!.getLayer(l));
      const features =
        existingLayers.length > 0
          ? map.current.queryRenderedFeatures(e.point, { layers: existingLayers })
          : [];

      if (!features || features.length === 0) {
        deselectAllFeatures(map, [SOURCE_IDS.polygon, SOURCE_IDS.lines, SOURCE_IDS.points]);
        if (onArtifactClickRef.current) onArtifactClickRef.current({});
        return;
      }

      const currentZoom = map.current.getZoom() || 0;

      let clickedFeature = features[0];
      if (currentZoom >= LAYER_VISIBILITY_ZOOM.points.minzoom) {
        const pointFeature = features.find((f) => f.layer?.id === LAYERS.points);
        if (pointFeature) clickedFeature = pointFeature;
      }

      if (
        currentZoom > LAYER_VISIBILITY_ZOOM.polygon.minzoom &&
        currentZoom < LAYER_VISIBILITY_ZOOM.lines.minzoom
      ) {
        selectFullElectricFeederPolygon(
          map,
          electricFeedersRef.current,
          clickedFeature,
          onArtifactClickRef.current
        );
      } else if (
        currentZoom > LAYER_VISIBILITY_ZOOM.lines.minzoom &&
        currentZoom < LAYER_VISIBILITY_ZOOM.points.minzoom
      ) {
        selectFullElectricFeederLines(
          map,
          electricFeedersRef.current,
          clickedFeature,
          onArtifactClickRef.current
        );
      } else if (currentZoom >= LAYER_VISIBILITY_ZOOM.points.minzoom) {
        selectArtifact(map, clickedFeature, onArtifactClickRef.current);
      }
    };

    m.on('click', handleClick);

    return () => {
      m.off('click', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Efecto 5: Export on zoom
  useEffect(() => {
    if (!map.current) return;

    const handleZoomChange = () => {
      exportImgCallback();
    };

    map.current.on('zoomend', handleZoomChange);

    return () => {
      if (map.current) {
        map.current.off('zoomend', handleZoomChange);
      }
    };
  }, [exportImgCallback]);

  return (
    <div className='map-wrap'>
      <div ref={mapContainer} className='map' />
    </div>
  );
};

export default Map;
